import type { CSSProperties } from "react";
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, Check, ChevronRight } from "lucide-react";

/* ─── Model catalogue (shared with RightAgentPanel) ────────────── */

export interface AIModel {
  id: string;
  name: string;
  /** Short label shown on the trigger chip */
  short: string;
  badge?: string;
}

export const MODEL_LIST: AIModel[] = [
  { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", short: "Sonnet 4.6", badge: "Best" },
  { id: "claude-3-5-haiku-20241022",  name: "Claude 3.5 Haiku",  short: "Haiku 3.5",  badge: "Fast" },
  { id: "gpt-4o",                     name: "GPT-4o",            short: "GPT-4o" },
  { id: "gemini-2.0-flash",           name: "Gemini 2.0 Flash",  short: "Gemini 2.0" },
  { id: "o1-preview",                 name: "o1-preview",        short: "o1",         badge: "Brain" },
];

export const DEFAULT_MODEL_ID = MODEL_LIST[0].id;

const POPOVER_W = 256;
const GAP = 8;

function styleFromRect(rect: DOMRect, direction: "up" | "down"): CSSProperties {
  let left = Math.max(GAP, Math.min(rect.left, window.innerWidth - POPOVER_W - GAP));
  if (direction === "down") {
    return {
      position: "fixed",
      top: rect.bottom + GAP,
      left,
      width: POPOVER_W,
      zIndex: 10_000,
    };
  }
  return {
    position: "fixed",
    top: rect.top - GAP,
    left,
    width: POPOVER_W,
    zIndex: 10_000,
    transform: "translateY(-100%)",
  };
}

/* ─── Props ─────────────────────────────────────────────────────── */

export interface ModelSelectorProps {
  value: string;
  onChange: (modelId: string) => void;
  /**
   * "inline" (default) — ghost chip that sits next to action buttons.
   *                       Low visual weight; doesn't compete with Primary buttons.
   * "panel"            — same popover but triggered from a larger surface.
   */
  variant?: "inline" | "panel";
  /** Popover direction relative to the trigger (viewport positioning). */
  direction?: "up" | "down";
  /** When true (default), menu renders via portal to <body> to escape overflow clipping. */
  usePortal?: boolean;
  /** Extra Tailwind classes on the trigger button */
  className?: string;
}

/* ─── Component ─────────────────────────────────────────────────── */

export default function ModelSelector({
  value,
  onChange,
  variant = "inline",
  direction = "up",
  usePortal = true,
  className = "",
}: ModelSelectorProps) {
  const [isOpen, setIsOpen]           = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef   = useRef<HTMLDivElement>(null);

  const selectedModel = MODEL_LIST.find((m) => m.id === value) ?? MODEL_LIST[0];

  const filtered = MODEL_LIST.filter((m) =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const reposition = useCallback(() => {
    if (typeof window === "undefined" || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPopoverStyle(styleFromRect(rect, direction));
  }, [direction]);

  function handleToggle() {
    setSearchQuery("");
    setIsOpen((prev) => {
      if (prev) {
        setPopoverStyle(null);
        return false;
      }
      if (containerRef.current) {
        setPopoverStyle(styleFromRect(containerRef.current.getBoundingClientRect(), direction));
      }
      return true;
    });
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverStyle(null);
      return;
    }
    reposition();
    const ro = new ResizeObserver(() => reposition());
    if (containerRef.current) ro.observe(containerRef.current);
    if (popoverRef.current) ro.observe(popoverRef.current);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [isOpen, direction, reposition, searchQuery, filtered.length]);

  /* Close on outside click (trigger + portal menu) */
  useEffect(() => {
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setIsOpen(false);
    }
    if (isOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const popoverInner = (
    <>
      {/* Search */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
        <Search size={12} className="text-gray-400 flex-shrink-0" />
        <input
          autoFocus
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search models…"
          className="flex-1 text-xs text-gray-700 bg-transparent placeholder-gray-400 focus:outline-none"
        />
      </div>

      {/* Model list */}
      <div className="max-h-48 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No models found</p>
        ) : (
          filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => { onChange(m.id); setIsOpen(false); }}
              className={[
                "w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors",
                value === m.id
                  ? "bg-indigo-50 text-indigo-700"
                  : "hover:bg-gray-50 text-gray-700",
              ].join(" ")}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">{m.name}</span>
                {m.badge && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-600 uppercase tracking-wide">
                    {m.badge}
                  </span>
                )}
              </div>
              {value === m.id && (
                <Check size={12} className="text-indigo-600 flex-shrink-0" />
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100">
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50 transition-colors"
        >
          <span>Add custom model</span>
          <ChevronRight size={12} />
        </button>
      </div>
    </>
  );

  const popoverClass =
    "rounded-xl border border-gray-200 shadow-lg bg-white/95 backdrop-blur-md overflow-hidden";

  return (
    <div ref={containerRef} className={`relative inline-flex ${className}`}>

      {/* ── Trigger chip ──────────────────────────────────────────── */}
      <button
        type="button"
        onClick={handleToggle}
        title={selectedModel.name}
        className={[
          "flex items-center gap-1 rounded-lg transition-colors select-none",
          variant === "inline"
            ? "text-[11px] font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 px-2 py-1"
            : "text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5",
        ].join(" ")}
      >
        <span className="max-w-[90px] truncate">{selectedModel.short}</span>
        <ChevronDown
          size={10}
          strokeWidth={2.5}
          className={`transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* ── Popover: portal (default) escapes overflow-hidden ancestors ─ */}
      {isOpen && usePortal && popoverStyle && typeof document !== "undefined" &&
        createPortal(
          <div ref={popoverRef} style={popoverStyle} className={popoverClass}>
            {popoverInner}
          </div>,
          document.body,
        )}

      {/* Inline fallback (no portal) */}
      {isOpen && !usePortal && (
        <div
          ref={popoverRef}
          className={[
            popoverClass,
            "absolute left-0 w-64 z-[210]",
            direction === "up" ? "bottom-full mb-2" : "top-full mt-2",
          ].join(" ")}
        >
          {popoverInner}
        </div>
      )}
    </div>
  );
}
